/**
 * Allowlist HTML sanitiser, run in the browser before `dangerouslySetInnerHTML`.
 *
 * The detail API already hardens its HTML fields server-side (removes
 * script/style/iframe and event handlers). This is the second, authoritative
 * pass: the browser's own parser builds the tree, then we walk it and keep only
 * a small set of formatting tags — everything else is unwrapped to its text,
 * every attribute is dropped except a safe `href` on `<a>`, and comments go.
 *
 * Returns a string safe to pass to `dangerouslySetInnerHTML`, or `''` when
 * nothing meaningful survives (so a section never renders for empty content).
 */

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'HR', 'DIV', 'SPAN',
  'UL', 'OL', 'LI',
  'H2', 'H3', 'H4', 'H5', 'H6',
  'STRONG', 'B', 'EM', 'I', 'U',
  'BLOCKQUOTE', 'SUP', 'SUB',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH',
  'A',
]);

function cleanElement(el, doc) {
  // Depth-first so children are handled before a parent might be unwrapped.
  for (const child of [...el.children]) cleanElement(child, doc);

  // Drop comment nodes at this level.
  for (const node of [...el.childNodes]) {
    if (node.nodeType === 8) node.remove();
  }

  if (!ALLOWED_TAGS.has(el.tagName)) {
    // Unwrap: replace the element with its own child nodes (keeps the text).
    const parent = el.parentNode;
    if (parent) {
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    }
    return;
  }

  // Strip every attribute, then re-add the few that are safe.
  const href = el.tagName === 'A' ? el.getAttribute('href') : null;
  for (const attr of [...el.attributes]) el.removeAttribute(attr.name);

  if (el.tagName === 'A' && href && /^https?:\/\//i.test(href.trim())) {
    el.setAttribute('href', href.trim());
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer');
  }
}

/**
 * Decode entity-encoded markup — `&lt;p&gt;`, double-encoded `&amp;lt;p&amp;gt;`,
 * numeric `&#60;` / `&#x3c;` — repeatedly until a pass changes nothing. Some API
 * fields store their HTML escaped; DOMParser alone would leave the tags as
 * visible text, which is the "raw &lt;p&gt; on screen" bug.
 */
function decodeEntities(input) {
  let s = String(input ?? '');
  for (let i = 0; i < 5; i += 1) {
    const next = s
      .replace(/&#x([0-9a-f]+);/gi, (m, h) => {
        const n = parseInt(h, 16);
        return Number.isFinite(n) && n <= 0x10ffff ? String.fromCodePoint(n) : m;
      })
      .replace(/&#(\d+);/g, (m, d) => {
        const n = Number(d);
        return Number.isFinite(n) && n <= 0x10ffff ? String.fromCodePoint(n) : m;
      })
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'")
      .replace(/&amp;/gi, '&');
    if (next === s) return s;
    s = next;
  }
  return s;
}

const LOOKS_TAGGED = /<\/?[a-z][a-z0-9]*(\s|\/?>)/i;

export function sanitizeHtml(input) {
  let raw = String(input ?? '');
  if (!raw.trim()) return '';

  // Unwrap entity-encoded markup before parsing (see decodeEntities).
  if (/&(?:amp;)*(?:lt|gt|#)/i.test(raw)) raw = decodeEntities(raw);

  let doc;
  try {
    doc = new DOMParser().parseFromString(raw, 'text/html');
  } catch {
    // No DOMParser (shouldn't happen in a browser) — fall back to plain text.
    return decodeEntities(raw).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  // Belt-and-braces: if the parsed text still looks like markup (a stubborn
  // double-encoding), decode it once more and re-parse.
  if (LOOKS_TAGGED.test(doc.body.textContent || '')) {
    try {
      doc = new DOMParser().parseFromString(decodeEntities(doc.body.textContent || ''), 'text/html');
    } catch { /* keep the first parse */ }
  }

  for (const child of [...doc.body.children]) cleanElement(child, doc);

  const html = doc.body.innerHTML.trim();
  // If tags-stripped content is only whitespace, treat as empty.
  return doc.body.textContent && doc.body.textContent.trim() ? html : '';
}
